import React from 'react';
import { Card, Box, Typography } from '@mui/material';
import { COLOR, FUENTE, UMBRALES } from '../../Recepcion/reportes/tokens';

/**
 * A qué oficinas se va y cómo sale la presentación en cada una.
 *
 * Sirve para detectar si hay una UDAI donde cuesta más presentar. El porcentaje se oculta cuando
 * la muestra es chica: con tres presentaciones, "67%" suena a mucho sobre nada.
 *
 * Van las dos bases y no una sola: "Turnos" son todas las idas a esa oficina (también las vistas,
 * las citaciones y las canceladas) y "Presentaciones" es lo que efectivamente se presentó ahí.
 * Con una sola columna aparecían filas del tipo "12 turnos, 0 ingresadas" que se leían como un
 * fracaso, cuando esas 12 idas eran vistas y no había nada que ingresar.
 *
 * La tasa la manda el backend. Antes la calculaba esta tabla dividiendo por ingresadas+rechazadas
 * mientras el backend usaba cantidad-pendientes: dos criterios de "resuelta" para el mismo número.
 *
 * @param {{data: Array<{udai, turnos, presentaciones, ingresadas, rechazadas, pendientes,
 *   tasaIngreso}>}} props
 */
const TablaUdai = ({ data }) => {
    const filas = data || [];

    const celda = { fontFamily: FUENTE, fontSize: 13, padding: '10px 12px', borderBottom: '1px solid #f1f5f9' };
    const encabezado = {
        ...celda, fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: '0.06em', color: COLOR.grisTexto, borderBottom: '1px solid #e2e8f0',
    };

    return (
        <Card variant="outlined" sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', boxShadow: 'none', padding: '18px 20px' }}>
            <Typography sx={{ fontFamily: FUENTE, fontSize: 15, fontWeight: 700, color: COLOR.negro, marginBottom: '2px' }}>
                Por UDAI
            </Typography>
            <Typography sx={{ fontFamily: FUENTE, fontSize: 12.5, color: COLOR.grisTexto, marginBottom: '14px' }}>
                Dónde se presenta y con qué suerte.
            </Typography>

            {filas.length === 0 ? (
                <Typography sx={{ fontFamily: FUENTE, fontSize: 13, color: COLOR.grisFrio, padding: '24px 0', textAlign: 'center' }}>
                    Sin datos en el período.
                </Typography>
            ) : (
                <Box sx={{ overflowX: 'auto' }}>
                    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <Box component="th" sx={{ ...encabezado, textAlign: 'left' }}>UDAI</Box>
                                <Box component="th" sx={{ ...encabezado, textAlign: 'right' }}>Idas</Box>
                                <Box component="th" sx={{ ...encabezado, textAlign: 'right' }}>Presentaciones</Box>
                                <Box component="th" sx={{ ...encabezado, textAlign: 'right' }}>Ingresadas</Box>
                                <Box component="th" sx={{ ...encabezado, textAlign: 'right' }}>Rechazadas</Box>
                                <Box component="th" sx={{ ...encabezado, textAlign: 'right' }}>Ingreso</Box>
                            </tr>
                        </thead>
                        <tbody>
                            {filas.map((f) => {
                                const tasa = f.tasaIngreso === null || f.tasaIngreso === undefined ? null : f.tasaIngreso;
                                return (
                                    <tr key={f.udai}>
                                        <Box component="td" sx={{ ...celda, fontWeight: 600, color: COLOR.negro }}>{f.udai}</Box>
                                        <Box component="td" sx={{ ...celda, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{f.turnos}</Box>
                                        <Box component="td" sx={{ ...celda, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{f.presentaciones ?? 0}</Box>
                                        <Box component="td" sx={{ ...celda, textAlign: 'right', color: COLOR.verde, fontVariantNumeric: 'tabular-nums' }}>{f.ingresadas}</Box>
                                        <Box component="td" sx={{ ...celda, textAlign: 'right', color: COLOR.rojo, fontVariantNumeric: 'tabular-nums' }}>{f.rechazadas}</Box>
                                        <Box component="td" sx={{ ...celda, textAlign: 'right', fontWeight: 700, color: tasa === null ? COLOR.grisFrio : COLOR.negro, fontVariantNumeric: 'tabular-nums' }}
                                             title={tasa === null ? `Menos de ${UMBRALES.muestraMinimaAnses} presentaciones resueltas: el porcentaje no diria nada` : ''}>
                                            {tasa === null ? '—' : `${tasa}%`}
                                        </Box>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Box>
                </Box>
            )}
        </Card>
    );
};

export default TablaUdai;
